import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LoanSubsidyApplication } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const SCHEMES = ['HFC_LOAN', 'RENTAL_SUBSIDY', 'DEPOSIT_GRANT'] as const;
const STATES = ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'DISBURSED'] as const;

export function Assistance() {
  const [scheme, setScheme] = useState('');
  const [state, setState] = useState('');
  const navigate = useNavigate();
  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (scheme) params.set('scheme', scheme);
    if (state) params.set('state', state);
    return `/api/v1/housing/assistance?${params.toString()}`;
  }, [scheme, state]);
  const list = useCursorList<LoanSubsidyApplication>(basePath);

  return (
    <div>
      <PageHeader title="Loan &amp; Subsidy Assistance" subtitle="HFC loans, rental subsidies, and deposit grants — submit -> review -> decision -> disbursement." />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Scheme" className="w-48">
          <select className="field-input" value={scheme} onChange={(e) => setScheme(e.target.value)}>
            <option value="">All schemes</option>
            {SCHEMES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="State" className="w-48">
          <select className="field-input" value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">All states</option>
            {STATES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">NIN</th>
              <th className="table-head">Scheme</th>
              <th className="table-head">Requested</th>
              <th className="table-head">Approved</th>
              <th className="table-head">State</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((a) => (
              <tr key={a.id} className="table-row-link" onClick={() => navigate(`/housing/assistance/${a.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{a.nin}</td>
                <td className="table-cell text-xs">{a.scheme.replaceAll('_', ' ')}</td>
                <td className="table-cell">{scr(a.amountRequested)}</td>
                <td className="table-cell">{a.amountApproved !== null ? scr(a.amountApproved) : '—'}</td>
                <td className="table-cell"><StatusBadge status={a.state} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading assistance applications…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No assistance applications found" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />
    </div>
  );
}

export function AssistanceDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('HOUSING_OFFICER') ?? false;
  const rowQ = useApiQuery<LoanSubsidyApplication>(`/api/v1/housing/assistance/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [amountApproved, setAmountApproved] = useState('');
  const [decisionNotes, setDecisionNotes] = useState('');

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      rowQ.reload();
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (rowQ.loading) return <Spinner label="Loading application…" />;
  if (rowQ.error || !rowQ.data) return <ErrorNote message={rowQ.error ?? 'Application not found'} />;
  const a = rowQ.data;

  return (
    <div>
      <BackLink to="/housing/assistance" />
      <PageHeader title={a.nin} subtitle={`${a.scheme.replaceAll('_', ' ')} · requested ${scr(a.amountRequested)}`} actions={<StatusBadge status={a.state} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      {note ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{note}</div> : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Bank details</h2>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Routing code</dt><dd className="font-medium text-slate-800">{a.bankRoutingCode}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Account no.</dt><dd className="font-medium text-slate-800">{a.bankAccountNo}</dd></div>
            {a.applicationId ? <div className="flex justify-between"><dt className="text-slate-500">Linked housing application</dt><dd className="font-mono text-xs text-slate-500">{a.applicationId}</dd></div> : null}
            {a.payIntentRefNo ? <div className="flex justify-between"><dt className="text-slate-500">Disbursement ref</dt><dd className="font-mono text-xs text-slate-500">{a.payIntentRefNo}</dd></div> : null}
          </dl>
          {a.decisionNotes ? (
            <div className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Decision notes</div>
              {a.decisionNotes}
            </div>
          ) : null}
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Officer actions</h2>
          {!isOfficer ? <p className="text-xs text-slate-400">Requires HOUSING_OFFICER.</p> : null}
          {isOfficer && a.state === 'SUBMITTED' ? (
            <button className="btn-primary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/assistance/${id}/review`), 'Moved to review.')}>
              Start review
            </button>
          ) : null}
          {isOfficer && a.state === 'UNDER_REVIEW' ? (
            <div className="space-y-2">
              <Field label="Amount approved (SCR, required to approve)">
                <input className="field-input" type="number" min="0" value={amountApproved} onChange={(e) => setAmountApproved(e.target.value)} />
              </Field>
              <Field label="Notes (required)">
                <textarea className="field-input" rows={2} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} />
              </Field>
              <div className="flex gap-2">
                <button
                  className="btn-success flex-1"
                  disabled={busy || !amountApproved || decisionNotes.length < 3}
                  onClick={() => act(() => api.post(`/api/v1/housing/assistance/${id}/decision`, { approve: true, amountApproved: Number(amountApproved), notes: decisionNotes }), 'Approved.')}
                >
                  Approve
                </button>
                <button
                  className="btn-danger flex-1"
                  disabled={busy || decisionNotes.length < 3}
                  onClick={() => act(() => api.post(`/api/v1/housing/assistance/${id}/decision`, { approve: false, notes: decisionNotes }), 'Rejected.')}
                >
                  Reject
                </button>
              </div>
            </div>
          ) : null}
          {isOfficer && a.state === 'APPROVED' ? (
            <button className="btn-primary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/assistance/${id}/disburse`), 'Disbursed.')}>
              Record disbursement
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
